import { useState, useEffect, useRef, useCallback } from "react";

interface SWRResult<T> {
  data: T | null;
  error: string;
  isLoading: boolean;
  mutate: () => void;
}

export const cache = new Map<string, { data: unknown; time: number }>();

// Subscribe to cache updates (for components that read cache directly)
const cacheListeners = new Set<() => void>();
const _origSet = cache.set;
cache.set = function (...args) {
  const result = _origSet.apply(this, args);
  for (const fn of cacheListeners) fn();
  return result;
};
export function onCacheChange(fn: () => void): () => void {
  cacheListeners.add(fn);
  return () => { cacheListeners.delete(fn); };
}

export function useSWR<T>(
  key: string | null,
  fetcher: (key: string) => Promise<T>,
  options?: { refreshInterval?: number; initialDelay?: number },
): SWRResult<T> {
  const cached = key ? cache.get(key) : null;
  const [data, setData] = useState<T | null>((cached?.data as T) ?? null);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(!cached);
  const activeKeyRef = useRef(key);

  // Always track the current key so stale responses are dropped
  activeKeyRef.current = key;

  const load = useCallback(() => {
    if (!key) return;
    const requestKey = key;

    // If we have cached data, show it immediately (stale) and revalidate
    const entry = cache.get(key);
    if (entry) {
      setData(entry.data as T);
      setIsLoading(false);
    }

    fetcher(key)
      .then((result) => {
        // Drop response if key has changed since this request started
        if (activeKeyRef.current !== requestKey) return;
        cache.set(requestKey, { data: result, time: Date.now() });
        setData(result);
        setError("");
        setIsLoading(false);
      })
      .catch((e: Error) => {
        if (activeKeyRef.current !== requestKey) return;
        if (!cache.has(requestKey)) {
          setError(e.message);
        }
        setIsLoading(false);
      });
  }, [key]);

  useEffect(() => {
    // On key change, immediately show cached data or reset
    const entry = key ? cache.get(key) : null;
    if (entry) {
      setData(entry.data as T);
      setError("");
      setIsLoading(false);
    } else if (key) {
      setData(null);
      setError("");
      setIsLoading(true);
    }

    load();

    const timers: ReturnType<typeof setTimeout>[] = [];
    if (options?.refreshInterval) {
      // First revalidation after initialDelay (default: same as interval)
      const firstDelay = options.initialDelay ?? options.refreshInterval;
      const t = setTimeout(() => {
        load();
        // Then regular interval
        const iv = setInterval(load, options.refreshInterval!);
        timers.push(iv as any);
      }, firstDelay);
      timers.push(t as any);
    }

    return () => {
      for (const t of timers) clearTimeout(t as any);
    };
  }, [key, load, options?.refreshInterval, options?.initialDelay]);

  return { data, error, isLoading: isLoading && !data, mutate: load };
}
